import { Type, proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { TwilioConfigInput } from '@pypestream/api-services/urql';

import {
  ProStudioEvents,
  ProStudioContext,
} from '../../prostudio.xstate.types';
import {
  Pype,
  PypeStyle,
  PypeBeta,
  PypeConfig,
} from '../../prostudio.api-service.types';
import {
  mutateChannel,
  checkForErrors,
  ChannelMutationProps,
} from '../../../utils';
import { loadBotPypes } from '../load-bot-pypes';

export interface CreatePypeChannelParams {
  pypeId: string;
  streamId: string;
  channelProps: {
    env: 'live' | 'sandbox';
    style?: PypeStyle;
    beta?: PypeBeta;
    name: string;
    customPypeCss?: string;
    config?: PypeConfig;
  };
  projectId: string;
  cb?: () => void;
  ctx: ProStudioContext;
  event: ProStudioEvents;
}

export interface CreateTwilioVoiceChannelParams {
  streamId: string;
  channelProps: {
    env: 'live' | 'sandbox';
    voiceAiConfig?: TwilioConfigInput;
    name: string;
  };
  projectId: string;
  uniqueCustomerId: string;
  ctx: ProStudioContext;
  event: ProStudioEvents;
  botId: string;
  cb?: () => void;
}

export const createPypeChannel = async ({
  pypeId,
  streamId,
  channelProps,
  projectId,
  cb,
  ctx,
  event,
}: CreatePypeChannelParams): Promise<{
  botPypes: Pype[];
  channelId: string;
  channelName: string;
  pypeId: string;
  pypeName: string;
}> => {
  const channel: ChannelMutationProps = {
    pypeId,
    streamId,
    env: channelProps.env,
    style: channelProps.style,
    beta: channelProps.beta,
    name: channelProps.name,
    customPypeCss: channelProps.customPypeCss,
    config: channelProps.config!,
  };

  const result = await mutateChannel({
    channel,
    requestAction: 'new',
    projectId,
    cb,
  });

  const { botPypes } = await loadBotPypes(ctx, event);

  return {
    botPypes,
    channelId: result?.id || streamId,
    channelName: channelProps.name,
    pypeId,
    pypeName: botPypes[0]?.name || '',
  };
};

export const createTwilioVoiceChannel = async ({
  streamId,
  channelProps,
  projectId,
  uniqueCustomerId,
  ctx,
  event,
  botId,
  cb,
}: CreateTwilioVoiceChannelParams): Promise<{
  botPypes: Pype[];
  channelId: string;
  channelName: string;
  pypeId: string;
  pypeName: string;
}> => {
  const mutation = {
    live: urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelId,
    sandbox:
      urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelIdSandbox,
  };
  const resp = await mutation[channelProps.env]({
    channelId: streamId,
    projectId,
    customerId: uniqueCustomerId,
    v3ConfigurationCustomerChannelInput: {
      config: {
        twilioConfig: channelProps.voiceAiConfig,
      },
      type: Type.TwilioVoice,
      startBot: botId,
      name: channelProps.name,
    },
  });

  checkForErrors(resp);

  cb?.();

  const { botPypes } = await loadBotPypes(ctx, event);

  // Get pypeId from the first bot pype
  const pypeId = botPypes[0]?.id || '';

  return {
    botPypes,
    channelId: streamId,
    channelName: channelProps.name,
    pypeId,
    pypeName: botPypes[0]?.name || '',
  };
};
